import { useState, type ReactNode } from 'react';
import { Alert, Box, Button, Skeleton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { api, type Announcement, type DeckSummary } from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { ConfirmDialog } from '../ConfirmDialog';
import { AnnouncementDialog } from './AnnouncementDialog';
import { ReadReportDialog } from './ReadReportDialog';
import { AnnouncementCard, EmptyState, Section } from './portalParts';
import { cardGrid } from './portalStyles';

interface Props {
  title: string;
  icon?: ReactNode;
  /** null while loading. */
  items: Announcement[] | null;
  onChanged: () => void;
  defaultCategory?: string;
  emptyText: string;
  /** Extra header action, e.g. "See all". */
  action?: ReactNode;
}

/** Announcement cards with the admin composer, edit and delete. */
export function AnnouncementBoard({ title, icon, items, onChanged, defaultCategory, emptyText, action }: Props) {
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const [composer, setComposer] = useState<{ open: boolean; editing: Announcement | null }>({ open: false, editing: null });
  const [sharedDecks, setSharedDecks] = useState<DeckSummary[]>([]);
  const [toDelete, setToDelete] = useState<Announcement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);

  const openComposer = (editing: Announcement | null) => {
    setComposer({ open: true, editing });
    void api
      .listDecks('shared')
      .then((r) => setSharedDecks(r.decks))
      .catch(() => setSharedDecks([]));
  };

  return (
    <Section
      title={title}
      icon={icon}
      action={
        <>
          {action}
          {isAdmin && (
            <Button size="small" variant="outlined" color="neutral" startIcon={<AddIcon />} onClick={() => openComposer(null)}>
              New announcement
            </Button>
          )}
        </>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {items === null ? (
        <Box sx={cardGrid}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={160} />
          ))}
        </Box>
      ) : items.length ? (
        <Box sx={cardGrid}>
          {items.map((a) => (
            <AnnouncementCard
              key={a.id}
              item={a}
              isAdmin={isAdmin}
              onEdit={() => openComposer(a)}
              onDelete={() => setToDelete(a)}
              onAcknowledge={() =>
                api.announcements
                  .markRead(a.id)
                  .then(onChanged)
                  .catch((err) => setError(err instanceof Error ? err.message : 'Could not record your confirmation'))
              }
              onShowReads={() => setReportId(a.id)}
            />
          ))}
        </Box>
      ) : (
        <EmptyState>{emptyText}</EmptyState>
      )}

      {isAdmin && (
        <AnnouncementDialog
          open={composer.open}
          editing={composer.editing}
          sharedDecks={sharedDecks}
          defaultCategory={defaultCategory}
          onClose={() => setComposer({ open: false, editing: null })}
          onSaved={onChanged}
        />
      )}
      {isAdmin && <ReadReportDialog announcementId={reportId} onClose={() => setReportId(null)} />}
      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Delete announcement?"
        message={`“${toDelete?.title ?? ''}” will be removed for everyone.`}
        confirmLabel="Delete"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          const a = toDelete;
          if (a)
            void api.announcements
              .remove(a.id)
              .then(onChanged)
              .catch((err) => setError(err instanceof Error ? err.message : 'Could not delete the announcement'));
        }}
      />
    </Section>
  );
}
